import { useQuizSubmissionTableColumns } from 'components/QuizSubmissions/Table';
import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useSnackbar } from 'contexts/snackbar';
import { useWebsocket } from 'contexts/websocket';
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import quizService from 'services/quiz';
import { SortOrder } from 'utils/constants';
import { resolveFeatureFlags } from 'utils/helper';
import { getQuizGradeSnapshot } from 'utils/quizScoring';
import { filterQuizSubmissions, getQuizSubmissionStatus, QuizSubmissionStatus } from 'utils/quizSubmissionList';
import { exportQuizSubmissionsToCSV } from 'utils/quizSubmissionsCsv';

export const PageContext = createContext();

const defaultSort = { order: SortOrder.ASC, orderBy: 'identifier' };
const initialFilters = { open: false, search: '', status: [], lmsSyncStatus: [], gradeRecord: [] };

const PageProvider = ({ children }) => {
    const { course, accountUsers, department } = useApp();
    const { auth } = useAuth();
    const { id } = useParams();
    const history = useHistory();
    const { setDialog, closeDialog } = useDialog();
    const { message } = useSnackbar();
    const { onMessage, setPingPayload } = useWebsocket();
    const { t } = useTranslation('quiz');
    const [data, setData] = useState(null);
    const [loadFailed, setLoadFailed] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [pushSubmissionIds, setPushSubmissionIds] = useState(null);
    const [checked, setChecked] = useState([]);
    const [filters, setFilters] = useState(initialFilters);
    const [sortState, setSortState] = useState(defaultSort);
    const scope = useRef(0);
    const refreshing = useRef(false);
    const resettingRef = useRef(false);
    const columns = useQuizSubmissionTableColumns();

    const load = useCallback(async () => {
        const request = ++scope.current;
        setData(null);
        setLoadFailed(false);
        setChecked([]);
        setFilters(initialFilters);
        setSortState(defaultSort);
        setResetting(false);
        setPushSubmissionIds(null);
        resettingRef.current = false;
        refreshing.current = false;
        try {
            const [quiz, submissions, batches] = await Promise.all([
                quizService.getQuiz(id),
                quizService.listQuizSubmissions(id),
                quizService.listQuizBatch(id),
            ]);
            if (request !== scope.current) return;
            if (
                quiz?.id !== id ||
                quiz.course_id !== auth.course_id ||
                !Array.isArray(submissions) ||
                !Array.isArray(batches)
            ) {
                throw new Error('Invalid quiz submissions data');
            }
            setData({ quiz, submissions, batches });
        } catch {
            if (request === scope.current) setLoadFailed(true);
        }
    }, [auth.course_id, id]);

    useEffect(() => {
        load();
        return () => {
            scope.current += 1;
        };
    }, [auth.owner_account_id, auth.role, load]);

    const quiz = useMemo(
        () => (data?.quiz ? resolveFeatureFlags({ ...data.quiz, type: 'quiz' }, department?.features) : null),
        [data?.quiz, department?.features]
    );
    const submissions = data?.submissions;
    const pendingBatchIds = useMemo(
        () => [
            ...new Set([
                ...(data?.batches ?? []).filter((batch) => !batch.is_finished && !batch.is_failed).map(({ id }) => id),
                ...(submissions ?? []).flatMap((submission) =>
                    (submission.feedback_regeneration?.active_batches ?? []).map(({ batch_id }) => batch_id)
                ),
            ]),
        ],
        [data?.batches, submissions]
    );

    const refresh = useCallback(async () => {
        if (refreshing.current || resettingRef.current) return;
        refreshing.current = true;
        const request = scope.current;
        try {
            const [submissions, batches] = await Promise.all([
                quizService.listQuizSubmissions(id),
                quizService.listQuizBatch(id),
            ]);
            if (request !== scope.current || !Array.isArray(submissions) || !Array.isArray(batches)) return;
            setData((current) => current && { ...current, submissions, batches });
        } catch {
            // Retain the list and retry on the next progress message or poll.
        } finally {
            if (request === scope.current) refreshing.current = false;
        }
    }, [id]);

    useEffect(() => setPingPayload(() => ({ batch_ids: pendingBatchIds })), [pendingBatchIds, setPingPayload]);
    useEffect(
        () =>
            onMessage((event) => {
                if (event.type === 'pong' && pendingBatchIds.some((id) => event.batches?.[id])) refresh();
            }),
        [onMessage, pendingBatchIds, refresh]
    );
    useEffect(() => {
        if (!pendingBatchIds.length) return;
        const interval = setInterval(refresh, 5000);
        return () => clearInterval(interval);
    }, [pendingBatchIds.length, refresh]);

    const sortedFilteredSubmissions = useMemo(() => {
        const filtered = filterQuizSubmissions(submissions ?? [], filters, quiz);
        const column = columns.find(({ id }) => id === sortState.orderBy);
        return column?.comparator
            ? column.comparator(sortState.order, filtered, { assignment: quiz, accountUsers: accountUsers ?? [] })
            : filtered;
    }, [accountUsers, columns, filters, quiz, sortState, submissions]);

    useEffect(() => {
        const visibleIds = new Set(sortedFilteredSubmissions.map(({ id }) => id));
        setChecked((current) =>
            current.every((id) => visibleIds.has(id)) ? current : current.filter((id) => visibleIds.has(id))
        );
    }, [sortedFilteredSubmissions]);

    const resetFilters = () => {
        setFilters((current) => ({ ...initialFilters, open: current.open, search: current.search }));
        setSortState(defaultSort);
    };

    const canReset =
        auth.role === 'instructor' &&
        Boolean(submissions?.length) &&
        !pendingBatchIds.length &&
        !resetting &&
        !pushSubmissionIds;
    const resetSubmissions = () => {
        if (!canReset || resettingRef.current) return;
        const dialogScope = scope.current;
        setDialog({
            open: true,
            message: t('submissions.reset-title', 'Erase Existing Quiz Data?'),
            description: t(
                'submissions.reset-description',
                'All imported submissions and associated grades/feedback for this quiz will be permanently deleted.'
            ),
            variant: 'destructive',
            actionButtonText: t('common:controls.continue', 'Continue'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: async () => {
                if (scope.current !== dialogScope || resettingRef.current) return;
                resettingRef.current = true;
                setResetting(true);
                closeDialog();
                const request = ++scope.current;
                refreshing.current = false;
                try {
                    await quizService.deleteQuizSubmissions(id);
                    if (request !== scope.current) return;
                    setData((current) => ({ ...current, submissions: [], batches: [] }));
                    setChecked([]);
                } catch {
                    if (request === scope.current)
                        message.error(
                            t('submissions.reset-error', 'Quiz submissions could not be reset. Please try again.')
                        );
                } finally {
                    if (request === scope.current) {
                        resettingRef.current = false;
                        setResetting(false);
                    }
                }
            },
            onCancel: closeDialog,
            onClose: closeDialog,
        });
    };

    const exportSubmissions = () => {
        if (!quiz || resettingRef.current) return;
        const submissionsToExport = checked.length ? submissions.filter(({ id }) => checked.includes(id)) : submissions;
        return exportQuizSubmissionsToCSV(course, quiz, submissionsToExport, accountUsers ?? [], t, submissions);
    };
    const pushableSubmissions = sortedFilteredSubmissions.filter(
        (submission) => (!checked.length || checked.includes(submission.id)) && getQuizGradeSnapshot(submission, quiz)
    );
    const canPushGrades =
        auth.role === 'instructor' &&
        process.env.REACT_APP_KIND === 'CANVAS' &&
        Boolean(quiz?.api_assignment_id) &&
        !resetting &&
        !pushSubmissionIds &&
        Boolean(pushableSubmissions.length);
    const pushGrades = () => {
        if (canPushGrades) setPushSubmissionIds(pushableSubmissions.map(({ id }) => id));
    };
    const pushScope = scope.current;
    const onPushed = (source, result) => {
        if (scope.current !== pushScope) return;
        setData(
            (current) =>
                current && {
                    ...current,
                    submissions: current.submissions.map((submission) => {
                        if (submission.id !== source.id) return submission;
                        const history = submission.grade_pushback_history ?? [];
                        return {
                            ...submission,
                            ...(submission.modified_on === source.modified_on && result.submission),
                            grade_pushback_history:
                                result.submission?.grade_pushback_history ??
                                (history.some(({ push_id }) => push_id === result.record.push_id)
                                    ? history
                                    : [...history, result.record]),
                        };
                    }),
                }
        );
    };
    const viewSubmission = (submission) => {
        if (resettingRef.current || getQuizSubmissionStatus(submission) !== QuizSubmissionStatus.REVIEWED) return;
        history.push(`/quizzes/${id}/submissions/${submission.id}`);
    };

    return (
        <PageContext.Provider
            value={{
                course,
                quiz,
                submissions,
                accountUsers: accountUsers ?? [],
                columns,
                filters,
                setFilters,
                resetFilters,
                checked,
                setChecked,
                sortState,
                handleSetSortState: setSortState,
                sortedFilteredSubmissions,
                loadFailed,
                load,
                resetting,
                canReset,
                resetSubmissions,
                exportSubmissions,
                pushGrades,
                pushSubmissionIds,
                closePushDialog: () => setPushSubmissionIds(null),
                onPushed,
                onClickBack: () => history.push(`/quizzes/${id}`),
                onClickViewSubmission: viewSubmission,
                onClickViewAll: () => {
                    const first = sortedFilteredSubmissions.find(
                        (submission) => getQuizSubmissionStatus(submission) === QuizSubmissionStatus.REVIEWED
                    );
                    if (first) viewSubmission(first);
                },
                canPushGrades,
            }}
        >
            {children}
        </PageContext.Provider>
    );
};

export default PageProvider;
